/**
 * 前端展示层的本地类型与轻量编解码。
 *
 * 这里**不复刻契约里的 zod schema**——那些 schema 的唯一真源是
 * `@spirit/contracts`，前端再抄一份就会在契约演进时悄悄漂移。
 * 本文件只放契约里没有、纯属 UI 内部的东西：
 * 1. 响应 `data` 不被契约冻结的端点所用的"忽略数据"schema；
 * 2. 查询参数/展示层的窄化助手。
 */

import { isAgeBand, isDomain, type AgeBand, type Domain, type ParseableSchema } from '@spirit/contracts';

/**
 * 忽略响应 `data` 的 schema。
 *
 * 用在契约只保证信封、不保证 `data` 形状的端点上：`POST /auth/logout`、
 * `PATCH /conversations/:id`、`DELETE /conversations/:id`。这些端点的返回值
 * 前端一个字都不用，但如果坚持用严格 schema 解析，服务端任何一次字段调整
 * 都会让"退出登录"这种基础操作报"响应结构不符合契约"——那是最糟的耦合方式。
 *
 * **只能交给 `apiRequest` 用**（它调 `schema.safeParse`）。别把它递给契约的
 * `unwrapEnvelope`：那个函数会把入参塞进 zod 的 `envelopeSchema` 并调用 `._parse`，
 * 手写对象没有这个方法，会直接抛 `TypeError: keyValidator._parse is not a function`。
 */
export const IgnoredDataSchema: ParseableSchema<null> = {
  safeParse(): { success: true; data: null } {
    return { success: true, data: null };
  },
};

/** 本地消息：服务端消息 + 仅供 UI 使用的发送态标记。 */
export interface LocalUserMessageDraft {
  content: string;
  domain: Domain | null;
  ageBand: AgeBand | null;
}

/**
 * 从任意值里挑出合法的 `domain`。
 *
 * 用在 URL 查询参数上：`/chat?domain=psychology` 里手打错一个字母，
 * 不应把整个会话创建流程打断，静默丢弃即可（与 Next 版的 `isDomain` 过滤一致）。
 */
export function parseDomain(value: unknown): Domain | null {
  return isDomain(value) ? value : null;
}

/** 同上，用于 `ageBand`。 */
export function parseAgeBand(value: unknown): AgeBand | null {
  return isAgeBand(value) ? value : null;
}

/** 上游模型 → 展示名；未知模型直接显示原名。 */
const MODEL_LABELS: Readonly<Record<string, string>> = {
  'deepseek-chat': 'DeepSeek Chat',
  'deepseek-reasoner': 'DeepSeek Reasoner',
};

/**
 * 展示用的模型名。
 *
 * `null` 一律渲染成「未知模型」而不是空字符串：气泡脚注里出现一个空洞，
 * 用户会以为界面坏了。
 */
export function modelLabel(model: string | null): string {
  if (model === null || model.length === 0) {
    return '未知模型';
  }
  return MODEL_LABELS[model] ?? model;
}

/** 路由置信度的中文说明（`meta` 帧里的 `routeConfidence` 用人话解释）。 */
export function confidenceLabel(confidence: number): string {
  if (confidence >= 0.8) {
    return '高置信';
  }
  if (confidence >= 0.5) {
    return '中置信';
  }
  return '低置信';
}
